// src/pages/AssessmentQuestions.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAssessment } from '../contexts/AssessmentContext';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Upload as UploadIcon,
  Visibility as VisibilityIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Edit as ReplaceIcon
} from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';

export default function AssessmentQuestions() {
  const navigate = useNavigate();
  const location = useLocation();
  const { 
    assessmentData, 
    updateQuestions, 
    updateAnswer, 
    getAnswerForQuestion,
    getAnsweredQuestionsCount,
    getTotalQuestionsCount
  } = useAssessment();

  const [currentStageTab, setCurrentStageTab] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [touchedComments, setTouchedComments] = useState({});
  const [uploading, setUploading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftMessage, setDraftMessage] = useState("");
  // File viewing dialog state
  const [filesDialog, setFilesDialog] = useState({ open: false, questionId: null, files: [] });
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [replacingFile, setReplacingFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(null); // Track which question just had successful upload

  // Support resume: get assessmentId and sessionToken from sessionStorage if present
  const sessionToken = assessmentData.sessionToken || sessionStorage.getItem('sessionToken') ;
  // Save as Draft handler
  const handleSaveDraft = async () => {
    setSavingDraft(true);
    setDraftMessage("");
    try {
      // Get answers from sessionStorage.assessmentData if available
      let answersArr = [];
      const assessmentDataRaw = sessionStorage.getItem('assessmentData');
      if (assessmentDataRaw) {
        const parsed = JSON.parse(assessmentDataRaw);
        if (parsed.answers && typeof parsed.answers === 'object') {
          answersArr = Object.values(parsed.answers).map(ans => ({
            question_id: ans.question_id,
            answer_option_code: ans.answer_option_code,
            free_text_comment: ans.free_text_comment
          }));
        }
      }
      // // Fallback to current state if nothing in sessionStorage
      if (answersArr.length === 0) {
        answersArr = (assessmentData.questions || [])
          .map(q => {
            const ans = getAnswerForQuestion(q.id) || {};
            return {
              question_id: q.id,
              answer_option_code: ans.answer_option_code,
              free_text_comment: ans.free_text_comment
            };
          })
          .filter(a => a.answer_option_code); // Only include answered
      }
      if (answersArr.length === 0) {
        setDraftMessage("Please select answers to save as draft.");
        setSavingDraft(false);
        setTimeout(() => setDraftMessage("") , 3000);
        return;
      }
      const response = await fetch(`https://py.taih.ai/api/assessments/${sessionToken}/draft/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers: answersArr })
      });
      if (response.ok) {
        setDraftMessage("Draft saved successfully.");
      } else {
        setDraftMessage("Failed to save draft. Please try again.");
      }
    } catch (err) {
      setDraftMessage("Error saving draft. Please try again.");
    } finally {
      setSavingDraft(false);
      setTimeout(() => setDraftMessage("") , 3000);
    }
  };

  const steps = ['User Registration', 'Instructions', 'Assessment Questions', 'Review & Submit', 'Results'];
  const stages = ['Design', 'Development', 'Deployment'];

  // Principle descriptions (keeping existing ones)
  const principleDescriptions = {
    'Universality and Inclusivity': 'AI systems should be designed and deployed in a manner that ensures their benefits are accessible across all segments of society. As a critical pillar of responsible AI, this principle emphasises that the value generated by AI must reach all corners of society, including underserved and marginalised communities, and should not deepen existing digital, social, or economic divides. Universality requires deliberate design choices, inclusive datasets, and deployment strategies that account for differences in language, ability, access, and socio-economic context.',
    'Valid and Reliable': 'AI systems must function as planned for their intended use without failure, generating accurate/validated findings.',
    'Safe': 'AI solutions must not endanger the well-being of any actors. It requires responsible design and deployment, as well as robust documentation attesting to its safety.',
    'Secure and Resilient': 'AI systems must ensure data availability and integrity and be able to withstand security threats like the exfiltration of models or intellectual property. They must be able to recover quickly and continue operating during and after disruptions/incidents.',
    'Accountable and Transparent': 'The information about AI systems and the output they generate must be readily available to the affected individuals, and AI actors must maintain a high level of accountability.',
    'Explainable and Interpretable': 'To ensure adequate oversight, AI systems must be easily explainable and interpretable to the parties involved in their development, deployment, and evaluation.',
    'Privacy-First': 'AI systems should be designed with privacy as a foundational principle which includes embedding privacy protections across the entire AI lifecycle, prioritising data minimisation, purpose limitation, and user control, and deploying appropriate technical and organizational safeguards to ensure that sensitive user data is collected, processed, and retained only to the extent necessary.',
    'Fair – with Bias Actively Managed': 'AI solutions must promote equality and equity by identifying, mitigating, and preventing all forms of bias, not only those that are overtly harmful or discriminatory. In line with NIST\'s framework, this includes addressing systemic bias arising from broader social or institutional structures, computational and statistical bias embedded in data, models, or design choices, and human-cognitive bias introduced through human judgment at different stages of the AI lifecycle. Fairness in AI therefore requires continuous assessment and proactive governance to ensure outcomes remain equitable across contexts and over time.'
  };

  // Define the order of principles (keeping existing)
  const principleOrder = [
    'Universality and Inclusivity',
    'Valid and Reliable',
    'Safe',
    'Secure and Resilient',
    'Accountable and Transparent',
    'Explainable and Interpretable',
    'Privacy-First',
    'Fair – with Bias Actively Managed'
  ];

  const answerOptions = [
    { code: 'INCOMPLETE', label: 'Incomplete', description: 'No evidence of the practice' },
    { code: 'DOCUMENTED', label: 'Documented', description: 'Policy exists but implementation is partial' },
    { code: 'OPERATIONAL', label: 'Operational', description: 'Practice is implemented and functioning' },
    { code: 'VERIFIED', label: 'Verified', description: 'Practice is verified by third party' }
  ];

  // Group questions by stage and sort by principle (keeping existing logic)
  const questionsByStage = React.useMemo(() => {
    const grouped = {
      'Design': [],
      'Development': [],
      'Deployment': []
    };
    
    assessmentData.questions.forEach(question => {
      if (grouped[question.stage]) {
        grouped[question.stage].push(question);
      }
    });
    
    // Sort questions in each stage by principle order
    Object.keys(grouped).forEach(stage => {
      grouped[stage].sort((a, b) => {
        const indexA = principleOrder.indexOf(a.principle);
        const indexB = principleOrder.indexOf(b.principle);
        
        // If both principles are in the order array, sort by their position
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        
        // If only one is in the array, it comes first
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        
        // If neither is in the array, maintain original order
        return 0;
      });
    });
    
    return grouped;
  }, [assessmentData.questions]);

  // Get current stage questions
  const currentStageQuestions = questionsByStage[stages[currentStageTab]] || [];

  // Check for navigation from review page (keeping existing logic)
  useEffect(() => {
    const startFromQuestion = location.state?.startFromQuestion;
    if (startFromQuestion !== undefined && startFromQuestion >= 0) {
      const question = assessmentData.questions[startFromQuestion];
      if (question) {
        const stageIndex = stages.indexOf(question.stage);
        if (stageIndex !== -1) {
          setCurrentStageTab(stageIndex);
          const stageQuestions = questionsByStage[question.stage];
          const indexInStage = stageQuestions.findIndex(q => q.id === question.id);
          if (indexInStage !== -1) {
            setCurrentQuestionIndex(indexInStage);
          }
        }
      }
    }
  }, [location.state, assessmentData.questions, questionsByStage, stages]);

  // Restore position based on answered questions (start from first unanswered or last answered)
  useEffect(() => {
    if (!loading && assessmentData.questions.length > 0 && !location.state?.startFromQuestion) {
      // Find the first unanswered question based on existing answers
      let lastAnsweredStage = 0;
      let lastAnsweredIndex = -1;
      
      // Go through each stage in order
      for (let stageIdx = 0; stageIdx < stages.length; stageIdx++) {
        const stageName = stages[stageIdx];
        const stageQuestions = questionsByStage[stageName] || [];
        
        for (let qIdx = 0; qIdx < stageQuestions.length; qIdx++) {
          const question = stageQuestions[qIdx];
          const answer = getAnswerForQuestion(question.id);
          
          // Check if this question has been answered (has both answer option and comment)
          if (answer?.answer_option_code && answer?.free_text_comment?.trim()) {
            lastAnsweredStage = stageIdx;
            lastAnsweredIndex = qIdx;
          } else {
            // Found first unanswered question - set position here
            setCurrentStageTab(stageIdx);
            setCurrentQuestionIndex(qIdx);
            return;
          }
        }
      }
      
      // If all questions answered, stay on the last answered question
      if (lastAnsweredIndex >= 0) {
        setCurrentStageTab(lastAnsweredStage);
        setCurrentQuestionIndex(lastAnsweredIndex);
      }
    }
  }, [loading, assessmentData.questions.length]);

  useEffect(() => {
    if (!sessionToken) {
      navigate('/');
      return;
    }
    
    if (assessmentData.questions.length === 0) {
      fetchQuestions();
    } else {
      setLoading(false);
    }
  }, [sessionToken, assessmentData.questions]);

  const fetchQuestions = async () => {
    try {
      const response = await fetch(`https://py.taih.ai/api/assessments/${sessionToken}/questions`);
      if (response.ok) {
        const data = await response.json();
        // If resuming, assume data is already a flat array, else reduce as before
        const allQuestions = Array.isArray(data)
          ? (data[0]?.questions ? data.reduce((acc, stage) => acc.concat(stage.questions), []) : data)
          : [];
        allQuestions.sort((a, b) => a.display_order - b.display_order);
        updateQuestions(allQuestions);
        allQuestions.forEach(q => {
  if (q.answer_option_code || q.free_text_comment) {
    updateAnswer(q.id, {
      question_id: q.id,
      answer_option_code: q.answer_option_code,
      free_text_comment: q.free_text_comment,
      answer_id: q.answer_id,
      has_files: q.has_evidence || false
    });
  }
});
 
        // Merge with existing assessmentData in sessionStorage if present

        const assessmentDataRaw = sessionStorage.getItem('assessmentData');
        let assessmentDataToStore = {};
        if (assessmentDataRaw) {
          try {
            assessmentDataToStore = JSON.parse(assessmentDataRaw);
          } catch {}
        }


        setLoading(false);
      } else {
        throw new Error('Failed to fetch questions');
      }
    } catch (err) {
      console.error('Error fetching questions:', err);
      setError('Failed to load questions. Please try again.');
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answerOptionCode) => {
    const currentAnswer = getAnswerForQuestion(questionId) || {};
    const updatedAnswer = {
      ...currentAnswer,
      answer_option_code: answerOptionCode,
      question_id: questionId
    };
    
    updateAnswer(questionId, updatedAnswer);
    
    if (error) {
      setError('');
    }
  };

  const handleCommentChange = (questionId, comment) => {
    const currentAnswer = getAnswerForQuestion(questionId) || {};
    const updatedAnswer = {
      ...currentAnswer,
      free_text_comment: comment,
      question_id: questionId
    };
    
    updateAnswer(questionId, updatedAnswer);
  };

  const handleFileUpload = async (questionId, event) => {
  const files = Array.from(event.target.files);
  console.log('Files selected:', files);
  
  if (files.length === 0) {
    console.log('No files selected');
    return;
  }

  setUploading(true);
  setError(''); // Clear any previous errors
  
  try {
    let uploadSuccess = false;
    
    for (const file of files) {
      console.log(`Uploading file: ${file.name}, size: ${file.size}`);
      
      const formData = new FormData();
      formData.append('file', file);

      console.log(`API call: POST /assessments/${sessionToken}/answers/${questionId}/evidence`);
      
      // Upload file using the evidence API
      const response = await fetch(`https://py.taih.ai/api/assessments/${sessionToken}/answers/${questionId}/evidence`, {
        method: 'POST',
        body: formData
      });

      console.log(`Response status: ${response.status}`);
      
      if (response.ok) {
        const result = await response.json();
        console.log('File upload result:', result);

        // Update answer with file information and answer_id
        const currentAnswer = getAnswerForQuestion(questionId) || {};
        const updatedAnswer = {
          ...currentAnswer,
          has_files: true,
          answer_id: result.answer_id, // Save answer_id for future API calls
          question_id: questionId
        };
        
        console.log('Updating answer with:', updatedAnswer);
        updateAnswer(questionId, updatedAnswer);
        uploadSuccess = true;
        
        // Show success message and auto-clear after 3 seconds
        setUploadSuccess(questionId);
        setTimeout(() => setUploadSuccess(null), 3000);
      } else {
        const errorText = await response.text();
        console.error(`Failed to upload ${file.name}:`, errorText);
        throw new Error(`Failed to upload file: ${file.name}. ${errorText}`);
      }
    }
    
    if (uploadSuccess) {
      // Reset the file input
      event.target.value = '';
      console.log('All files uploaded successfully');
    }
    
  } catch (error) {
    console.error('Error uploading files:', error);
    setError(`Failed to upload files: ${error.message}`);
  } finally {
    setUploading(false);
  }
};

  const getAnsweredCountForStage = (stageName) => {
    const stageQuestions = questionsByStage[stageName] || [];
    return stageQuestions.filter(q => {
      const answer = getAnswerForQuestion(q.id);
      return answer?.answer_option_code && answer?.free_text_comment?.trim();
    }).length;
  };

  const handleNext = () => {
    const currentQuestion = currentStageQuestions[currentQuestionIndex];
    const currentAnswer = getAnswerForQuestion(currentQuestion.id);
    
    // Validate both answer option and comment
    if (!currentAnswer?.answer_option_code) {
      setError('Please select an answer before proceeding.');
      return;
    }
    
    if (!currentAnswer?.free_text_comment?.trim()) {
      setError('Please provide a comment before proceeding.');
      // Mark as touched to show error
      setTouchedComments(prev => ({ ...prev, [currentQuestion.id]: true }));
      return;
    }

    if (currentQuestionIndex < currentStageQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setError('');
    } else if (currentStageTab < stages.length - 1) {
      setCurrentStageTab(prev => prev + 1);
      setCurrentQuestionIndex(0);
      setError('');
    } else {
      navigate('/assessment/review');
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setError('');
    } else if (currentStageTab > 0) {
      setCurrentStageTab(prev => prev - 1);
      const prevStageQuestions = questionsByStage[stages[currentStageTab - 1]] || [];
      setCurrentQuestionIndex(prevStageQuestions.length - 1);
      setError('');
    }
  };

  const handleStageTabChange = (event, newValue) => {
    setCurrentStageTab(newValue);
    setCurrentQuestionIndex(0);
    setError('');
  };

  const isStageComplete = (stageName) => {
    const stageQuestions = questionsByStage[stageName] || [];
    const answeredCount = getAnsweredCountForStage(stageName);
    return answeredCount === stageQuestions.length && stageQuestions.length > 0;
  };

  // Loading and error states (keeping existing logic)
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading questions...</Typography>
      </Box>
    );
  }

  if (assessmentData.questions.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">No questions available for assessment.</Alert>
        <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Go to Home
        </Button>
      </Container>
    );
  }

  const currentQuestion = currentStageQuestions[currentQuestionIndex];
  const currentAnswer = currentQuestion ? getAnswerForQuestion(currentQuestion.id) || {} : {};
  const stageProgress = currentStageQuestions.length > 0 
    ? ((currentQuestionIndex + 1) / currentStageQuestions.length) * 100 
    : 0;

  const handleViewFiles = async (questionId) => {
    const answer = getAnswerForQuestion(questionId);
    if (!answer?.answer_id || !sessionToken) {
      console.error('No answer_id or session token available');
      return;
    }
    setLoadingFiles(true);
    setFilesDialog({ open: true, questionId, files: [] });
    try {
      const response = await fetch(`https://py.taih.ai/api/assessments/${sessionToken}/answers/${answer.answer_id}/evidence`);
      if (response.ok) {
        const files = await response.json();
        setFilesDialog({ open: true, questionId, files });
      } else {
        setError('Failed to fetch files. Please try again.');
      }
    } catch (error) {
      setError('Error fetching files. Please try again.');
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleReplaceFile = async (questionId, evidenceId) => {
    // Create a temporary file input
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = false;
    fileInput.accept = '*/*';
    fileInput.style.display = 'none';
    
    document.body.appendChild(fileInput);

    fileInput.onchange = async (event) => {
      const file = event.target.files[0];
      document.body.removeChild(fileInput);
      
      if (!file || !sessionToken) return;

      setReplacingFile(evidenceId);
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(
          `https://py.taih.ai/api/assessments/${sessionToken}/answers/${questionId}/evidence/replace?evidence_id=${evidenceId}`, 
          {
            method: 'POST',
            body: formData
          }
        );

        if (response.ok) {
          const result = await response.json();
          console.log('File replaced:', result);
          
          const currentAnswer = getAnswerForQuestion(questionId) || {};
          const updatedAnswer = {
            ...currentAnswer,
            answer_id: result.answer_id,
            question_id: questionId
          };
          updateAnswer(questionId, updatedAnswer);

          // Refresh the files list
          handleViewFiles(questionId);
        } else {
          throw new Error('Failed to replace file');
        }
      } catch (error) {
        console.error('Error replacing file:', error);
        setError('Failed to replace file. Please try again.');
      } finally {
        setReplacingFile(null);
      }
    };

    fileInput.oncancel = () => {
      document.body.removeChild(fileInput);
    };

    fileInput.click();
  };

  const closeFilesDialog = () => {
    setFilesDialog({ open: false, questionId: null, files: [] });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#004494', color: 'white', py: 2 }}>
        <Container>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative'
          }}>
            <IconButton 
              onClick={() => navigate('/')}
              sx={{ 
                color: 'white', 
                position: 'absolute',
                left: 0,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
              Assessment Questions
            </Typography>

            <Box sx={{ position: 'absolute', right: 0 }}>
              <Typography variant="caption" sx={{ color: '#90caf9' }}>
                ✓ Auto-saved locally
              </Typography>
            </Box>
            </Box>

          {/* Progress Stepper */}
          <Box sx={{ mt: 3 }}>
            <Stepper activeStep={2} alternativeLabel>
              {steps.map((label, idx) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': { color: 'white !important' },
                      '& .MuiStepIcon-root': {
                        color: idx <= 2 ? '#fff !important' : undefined
                      },
                      '& .MuiStepIcon-text': {
                        fill: idx <= 2 ? '#004494' : undefined
                      }
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
          {/* Stage Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={currentStageTab} 
              onChange={handleStageTabChange}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'none'
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#004494'
                },
                '& .Mui-selected': {
                  color: '#004494 !important'
                }
              }}
            >
              {stages.map((stage) => (
                <Tab 
                  key={stage}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {stage}
                      {isStageComplete(stage) && (
                        <CheckCircleIcon sx={{ fontSize: 20, color: '#4caf50' }} />
                      )}
                      <Chip 
                        label={`${getAnsweredCountForStage(stage)}/${questionsByStage[stage]?.length || 0}`}
                        size="small"
                        color={isStageComplete(stage) ? 'success' : 'default'}
                        sx={{ 
                          ml: 1,
                          backgroundColor: isStageComplete(stage) ? '#4caf50' : '#e0e0e0',
                          color: isStageComplete(stage) ? 'white' : '#666'
                        }}
                      />
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>

          {currentStageQuestions.length === 0 ? (
            // Empty stage UI (keeping existing)
            <>
              <Alert severity="info" sx={{ mb: 4 }}>
                No questions available for the {stages[currentStageTab]} stage.
              </Alert>

              <Box sx={{ mb: 4 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Total Progress: {getAnsweredQuestionsCount()}/{getTotalQuestionsCount()}
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={(getAnsweredQuestionsCount() / getTotalQuestionsCount()) * 100} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#004494'
                    }
                  }}
                />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  onClick={handlePrevious}
                  disabled={currentStageTab === 0}
                  startIcon={<ArrowBackIcon />}
                  variant="outlined"
                  sx={{
                    borderColor: '#004494',
                    color: '#004494',
                    '&:hover': {
                      borderColor: '#003366',
                      color: '#003366',
                      backgroundColor: 'rgba(0, 68, 148, 0.04)'
                    }
                  }}
                >
                  Previous Stage
                </Button>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    onClick={() => navigate('/assessment/review')}
                    variant="outlined"
                    disabled={getAnsweredQuestionsCount() === 0}
                    sx={{ 
                      minWidth: 160,
                      borderColor: '#004494',
                      color: '#004494',
                      '&:hover': {
                        borderColor: '#003366',
                        color: '#003366',
                        backgroundColor: 'rgba(0, 68, 148, 0.04)'
                      }
                    }}
                  >
                    Review Answers ({getAnsweredQuestionsCount()})
                  </Button>

                  <Button
                    onClick={() => {
                      if (currentStageTab < stages.length - 1) {
                        setCurrentStageTab(prev => prev + 1);
                        setCurrentQuestionIndex(0);
                      } else {
                        navigate('/assessment/review');
                      }
                    }}
                    endIcon={<ArrowForwardIcon />}
                    variant="contained"
                    size="large"
                    sx={{
                      backgroundColor: '#004494',
                      '&:hover': {
                        backgroundColor: '#003366'
                      }
                    }}
                  >
                    {currentStageTab === stages.length - 1 ? 'Proceed to Review' : 'Next Stage'}
                  </Button>
                </Box>
              </Box>
            </>
          ) : currentQuestion ? (
            <>
              {/* Question progress and content */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {stages[currentStageTab]} - Question {currentQuestionIndex + 1} of {currentStageQuestions.length}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Total Progress: {getAnsweredQuestionsCount()}/{getTotalQuestionsCount()}
                  </Typography>
                  <Chip 
                    label={currentQuestion.principle} 
                    sx={{
                      backgroundColor: '#004494',
                      color: 'white',
                      '& .MuiChip-label': {
                        color: 'white'
                      }
                    }}
                    variant="filled" 
                    size="small"
                  />
                </Box>
              </Box>

              <Box sx={{ mb: 4 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={stageProgress} 
                  sx={{ 
                    height: 8, 
                    borderRadius: 4,
                    backgroundColor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#004494'
                    }
                  }}
                />
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Principle Description Accordion */}
              {principleDescriptions[currentQuestion.principle] && (
                <Accordion 
                  sx={{ 
                    mb: 3, 
                    border: '1px solid', 
                    borderColor: 'divider',
                    borderRadius: '8px !important',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '0 0 24px 0'
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#004494' }} />}
                    sx={{
                      bgcolor: 'rgba(0, 68, 148, 0.05)',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      minHeight: '56px',
                      '&:hover': { 
                        bgcolor: 'rgba(0, 68, 148, 0.1)' 
                      },
                      '&.Mui-expanded': {
                        minHeight: '56px'
                      },
                      '& .MuiAccordionSummary-content': {
                        margin: '12px 0',
                        '&.Mui-expanded': {
                          margin: '12px 0'
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon sx={{ color: '#004494', fontSize: 20 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#004494' }}>
                        {currentQuestion.principle} Principle
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ bgcolor: 'background.paper', p: 3 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.7, color: 'text.secondary' }}>
                      {principleDescriptions[currentQuestion.principle]}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              {/* Question Card */}
              <Card sx={{ mb: 4, border: '2px solid #004494' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ mb: 4, fontWeight: 600, lineHeight: 1.4 }}>
                    {currentQuestion.question_text}
                  </Typography>

                  {/* Answer Options */}
                  <FormControl component="fieldset" fullWidth sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                      Select your answer: <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <RadioGroup
                      value={currentAnswer.answer_option_code || ''}
                      onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    >
                      {answerOptions.map((option) => (
                        <FormControlLabel
                          key={option.code}
                          value={option.code}
                          control={
                            <Radio 
                              sx={{
                                color: '#004494',
                                '&.Mui-checked': {
                                  color: '#004494',
                                },
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {option.label}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {option.description}
                              </Typography>
                            </Box>
                          }
                          sx={{
                            mb: 2,
                            p: 3,
                            border: '1px solid',
                            borderColor: currentAnswer.answer_option_code === option.code ? '#004494' : 'grey.300',
                            borderRadius: 2,
                            bgcolor: currentAnswer.answer_option_code === option.code ? 'rgba(0, 68, 148, 0.05)' : 'transparent',
                            '&:hover': {
                              bgcolor: 'grey.50',
                              borderColor: '#004494'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        />
                      ))}
                    </RadioGroup>
                    </FormControl>

                  <Divider sx={{ my: 4 }} />

                  {/* Comment Section - Mandatory */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                      Comment <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Please provide additional comments, explanations, or context related to your answer. This field is required."
                      value={currentAnswer.free_text_comment || ''}
                      onChange={(e) => handleCommentChange(currentQuestion.id, e.target.value)}
                      onBlur={() => setTouchedComments(prev => ({ ...prev, [currentQuestion.id]: true }))}
                      error={touchedComments[currentQuestion.id] && !currentAnswer.free_text_comment?.trim()}
                      helperText={
                        touchedComments[currentQuestion.id] && !currentAnswer.free_text_comment?.trim() 
                          ? "Comment is required" 
                          : ""
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'grey.50',
                          '&:hover': {
                            backgroundColor: 'grey.100',
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'white',
                          },
                          '&.Mui-error': {
                            backgroundColor: 'error.50',
                          },
                          '&:hover fieldset': {
                            borderColor: '#004494',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#004494',
                          },
                        },
                      }}
                    />
                  </Box>

                  <Divider sx={{ my: 4 }} />

                 {/* Document Upload Section */}
<Box>
  <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
    Supporting Documents
    <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}>
      (Optional)
    </Typography>
  </Typography>
  
  <Box sx={{ mb: 2 }}>
    <input
      accept="*/*"
      style={{ display: 'none' }}
      id={`upload-button-${currentQuestion.id}`}
      multiple
      type="file"
      onChange={(e) => handleFileUpload(currentQuestion.id, e)}
      disabled={uploading}
    />
    <label htmlFor={`upload-button-${currentQuestion.id}`}>
      <Button
        variant="outlined"
        component="span"
        startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
        disabled={uploading}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          py: 1.5,
          px: 3,
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: '#004494',
          color: '#004494',
          '&:hover': {
            borderStyle: 'dashed',
            borderWidth: 2,
            backgroundColor: 'rgba(0, 68, 148, 0.05)',
            borderColor: '#003366'
          }
        }}
      >
        {uploading ? 'Uploading...' : 'Upload Document'}
      </Button>
    </label>
  </Box>
  
  {/* Show upload status */}
  {uploading && (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ color: '#004494', fontWeight: 500 }}>
        📤 Uploading files...
      </Typography>
    </Box>
  )}
  
  {/* Show upload success message - auto clears after 3 sec */}
  {uploadSuccess === currentQuestion.id && (
    <Alert 
      severity="success" 
      sx={{ 
        py: 1, 
        mb: 2,
        '& .MuiAlert-icon': {
          color: '#4caf50'
        }
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        ✓ Files uploaded successfully
      </Typography>
    </Alert>
  )}
  
  {/* View Files button - shown when files exist */}
  {currentAnswer.has_files && !uploading && (
    <Box sx={{ mb: 2 }}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<VisibilityIcon />}
        sx={{ 
          textTransform: 'none', 
          fontWeight: 600,
          borderColor: '#004494',
          color: '#004494',
          '&:hover': {
            borderColor: '#003366',
            color: '#003366',
            backgroundColor: 'rgba(0, 68, 148, 0.04)'
          }
        }}
        onClick={() => handleViewFiles(currentQuestion.id)}
      >
        View Files
      </Button>
    </Box>
  )}
</Box>
                </CardContent>
              </Card>

              {/* Navigation Buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 2 },
                    width: '100%',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                  }}
                >
                  <Button
                    onClick={handlePrevious}
                    disabled={currentStageTab === 0 && currentQuestionIndex === 0}
                    startIcon={<ArrowBackIcon />}
                    variant="outlined"
                    size="large"
                    sx={{ 
                      width: { xs: '100%', sm: 'auto' },
                      borderColor: '#004494',
                      color: '#004494',
                      '&:hover': {
                        borderColor: '#003366',
                        color: '#003366',
                        backgroundColor: 'rgba(0, 68, 148, 0.04)'
                      }
                    }}
                  >
                    Previous
                  </Button>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 2, sm: 2 },
                      flex: 1,
                      justifyContent: { xs: 'stretch', sm: 'flex-end' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                    }}
                  >
                    <Button
                      onClick={() => navigate('/assessment/review')}
                      variant="outlined"
                      disabled={getAnsweredQuestionsCount() === 0}
                      sx={{ 
                        minWidth: 160, 
                        width: { xs: '100%', sm: 'auto' },
                        borderColor: '#004494',
                        color: '#004494',
                        '&:hover': {
                          borderColor: '#003366',
                          color: '#003366',
                          backgroundColor: 'rgba(0, 68, 148, 0.04)'
                        }
                      }}
                    >
                      Review Answers ({getAnsweredQuestionsCount()})
                    </Button>

                    <Button
                      onClick={handleSaveDraft}
                      variant="outlined"
                      disabled={savingDraft || stages.reduce((total, stage) => total + getAnsweredCountForStage(stage), 0) === 0}
                      sx={{ 
                        minWidth: 140, 
                        width: { xs: '100%', sm: 'auto' },
                        borderColor: '#004494',
                        color: '#004494',
                        '&:hover': {
                          borderColor: '#003366',
                          color: '#003366',
                          backgroundColor: 'rgba(0, 68, 148, 0.04)'
                        }
                      }}
                    >
                      {savingDraft ? 'Saving...' : 'Save as Draft'}
                    </Button>
                    {draftMessage && (
                      <Box sx={{ mt: 2, width: '100%' }}>
                        <Alert severity={draftMessage.includes('success') ? 'success' : 'error'} sx={{ width: '100%' }}>{draftMessage}</Alert>
                      </Box>
                    )}
                   <Button
  onClick={handleNext}
  disabled={!currentAnswer.answer_option_code || !currentAnswer.free_text_comment?.trim()}
  endIcon={<ArrowForwardIcon />}
  variant="contained"
  size="large"
  sx={{ 
    minWidth: 120, 
    width: { xs: '100%', sm: 'auto' },
    backgroundColor: '#004494',
    color: 'white',
    '&:hover': {
      backgroundColor: '#003366',
      color: 'white'
    },
    '&:disabled': {
      backgroundColor: '#ccc',
      color: 'rgba(0, 0, 0, 0.26)'
    }
  }}
>
  {currentStageTab === stages.length - 1 &&
  currentQuestionIndex === currentStageQuestions.length - 1
    ? 'Complete'
    : 'Next'}
</Button>
                  </Box>
                </Box>

              </Box>
            </>
          ) : null}
        </Paper>
      </Container>

      {/* Files Dialog */}
      <Dialog 
        open={filesDialog.open} 
        onClose={closeFilesDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Supporting Documents
          </Typography>
          <IconButton onClick={closeFilesDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {loadingFiles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#004494' }} />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading files...</Typography>
            </Box>
          ) : filesDialog.files.length > 0 ? (
            <List>
              {filesDialog.files.map((file, index) => (
                <ListItem 
                  key={file.evidence_id || index} 
                  sx={{ borderBottom: index < filesDialog.files.length - 1 ? '1px solid' : 'none', borderColor: 'divider', py: 2 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon sx={{ color: '#004494', fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {file.file_name}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {file.size_bytes ? `${(file.size_bytes / 1024).toFixed(2)} KB` : ''} • {file.mime_type}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={replacingFile === file.evidence_id ? <CircularProgress size={16} /> : <ReplaceIcon />}
                      onClick={() => handleReplaceFile(filesDialog.questionId, file.evidence_id)}
                      disabled={replacingFile === file.evidence_id}
                      sx={{ 
                        textTransform: 'none',
                        minWidth: 'auto',
                        px: 1.5,
                        borderColor: '#004494',
                        color: '#004494',
                        '&:hover': {
                          borderColor: '#003366',
                          color: '#003366',
                          backgroundColor: 'rgba(0, 68, 148, 0.04)'
                        }
                      }}
                    >
                     {replacingFile === file.evidence_id ? 'Replacing...' : 'Replace'}
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                No files found for this question.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={closeFilesDialog} 
            variant="contained"
            sx={{
              backgroundColor: '#004494',
              '&:hover': {
                backgroundColor: '#003366'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}